<template>
  <div class="inquiries-admin">
    <div class="toolbar">
      <div class="toolbar-left">
        <input
          v-model.trim="keyword"
          type="search"
          class="search-input"
          placeholder="搜索姓名、邮箱、网站、内容…"
          @keyup.enter="loadList"
        />
        <button type="button" class="btn btn-secondary" @click="loadList">搜索</button>
        <button v-if="keyword" type="button" class="btn btn-ghost" @click="resetSearch">重置</button>
      </div>
      <span v-if="unreadCount" class="unread-badge">{{ unreadCount }} 条未读</span>
    </div>

    <p v-if="error" class="alert alert-error">{{ error }}</p>
    <p v-if="success" class="alert alert-success">{{ success }}</p>

    <div v-if="loading" class="loading">加载中…</div>

    <div v-else class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th class="col-status">状态</th>
            <th>姓名</th>
            <th>邮箱</th>
            <th class="col-website">网站</th>
            <th class="col-time">提交时间</th>
            <th class="col-actions">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!items.length">
            <td colspan="6" class="empty-cell">暂无咨询消息</td>
          </tr>
          <tr
            v-for="row in items"
            :key="row.id"
            :class="{ 'row-unread': !row.is_read }"
          >
            <td class="col-status">
              <span :class="row.is_read ? 'badge badge-read' : 'badge badge-unread'">
                {{ row.is_read ? '已读' : '未读' }}
              </span>
            </td>
            <td>{{ row.name }}</td>
            <td>
              <a :href="'mailto:' + row.email" class="email-link">{{ row.email }}</a>
            </td>
            <td class="col-website">{{ row.website || '—' }}</td>
            <td class="col-time">{{ formatTime(row.created_at) }}</td>
            <td class="col-actions">
              <button type="button" class="btn-link" @click="openDetail(row)">查看</button>
              <button type="button" class="btn-link danger" @click="confirmDelete(row)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p class="table-footer">共 {{ items.length }} 条 · 按提交时间倒序</p>
    </div>

    <div v-if="detailVisible" class="modal-mask" @click.self="closeDetail">
      <div class="modal modal-wide">
        <h3 class="modal-title">咨询详情</h3>
        <div v-if="detail" class="detail-body">
          <dl class="detail-grid">
            <dt>姓名</dt>
            <dd>{{ detail.name }}</dd>
            <dt>邮箱</dt>
            <dd>
              <a :href="'mailto:' + detail.email">{{ detail.email }}</a>
            </dd>
            <dt>网站</dt>
            <dd>{{ detail.website || '—' }}</dd>
            <dt>提交时间</dt>
            <dd>{{ formatTime(detail.created_at) }}</dd>
          </dl>
          <div class="detail-message">
            <h4>留言内容</h4>
            <pre class="message-text">{{ detail.message }}</pre>
          </div>
        </div>
        <div class="modal-actions">
          <button
            v-if="detail && !detail.is_read"
            type="button"
            class="btn btn-secondary"
            @click="markRead(detail.id)"
          >
            标为已读
          </button>
          <button type="button" class="btn btn-ghost" @click="closeDetail">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  deleteInquiry,
  fetchInquiries,
  fetchInquiry,
  markInquiryRead
} from '@/api/inquiries'

export default {
  name: 'AdminInquiries',
  data() {
    return {
      items: [],
      unreadCount: 0,
      keyword: '',
      loading: false,
      error: '',
      success: '',
      detailVisible: false,
      detail: null
    }
  },
  created() {
    this.loadList()
  },
  methods: {
    formatTime(value) {
      if (!value) return '—'
      const d = new Date(String(value).replace(' ', 'T'))
      if (Number.isNaN(d.getTime())) return value
      return d.toLocaleString('zh-CN', { hour12: false })
    },
    messagePreview(text) {
      const t = (text || '').replace(/\s+/g, ' ').trim()
      if (!t) return '—'
      return t.length > 60 ? `${t.slice(0, 60)}…` : t
    },
    async loadList() {
      this.loading = true
      this.error = ''
      try {
        const res = await fetchInquiries(this.keyword)
        this.items = res.data?.items || []
        this.unreadCount = res.data?.unread ?? 0
      } catch (err) {
        this.error = err.message || '加载失败'
      } finally {
        this.loading = false
      }
    },
    resetSearch() {
      this.keyword = ''
      this.loadList()
    },
    async openDetail(row) {
      this.error = ''
      try {
        const res = await fetchInquiry(row.id)
        this.detail = res.data
        this.detailVisible = true
        await this.loadList()
      } catch (err) {
        this.error = err.message || '加载详情失败'
      }
    },
    closeDetail() {
      this.detailVisible = false
      this.detail = null
    },
    async markRead(id) {
      try {
        await markInquiryRead(id, true)
        if (this.detail?.id === id) {
          this.detail = { ...this.detail, is_read: true }
        }
        await this.loadList()
        this.flashSuccess('已标为已读')
      } catch (err) {
        this.error = err.message || '操作失败'
      }
    },
    confirmDelete(row) {
      if (!window.confirm(`确定删除「${row.name}」的咨询？`)) return
      this.deleteRow(row.id)
    },
    async deleteRow(id) {
      this.error = ''
      try {
        await deleteInquiry(id)
        if (this.detail?.id === id) this.closeDetail()
        this.flashSuccess('已删除')
        await this.loadList()
      } catch (err) {
        this.error = err.message || '删除失败'
      }
    },
    flashSuccess(message) {
      this.success = message
      setTimeout(() => {
        this.success = ''
      }, 3000)
    }
  }
}
</script>

<style scoped>
.inquiries-admin {
  max-width: 1200px;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.search-input {
  min-width: 260px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
}

.unread-badge {
  font-size: 13px;
  color: #c62828;
  background: #fdecea;
  padding: 6px 12px;
  border-radius: 20px;
}

.loading {
  padding: 40px;
  text-align: center;
  color: #888;
}

.alert {
  padding: 10px 14px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
}

.alert-error {
  background: #fdecea;
  color: #c62828;
}

.alert-success {
  background: #e8f5e9;
  color: #2e7d32;
}

.table-wrap {
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th,
.data-table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}

.data-table th {
  background: #fafafa;
  font-weight: 600;
  color: #444;
}

.row-unread {
  background: #fffde7;
}

.col-status {
  width: 72px;
}

.col-website {
  max-width: 160px;
  word-break: break-all;
}

.col-time {
  width: 160px;
  white-space: nowrap;
}

.col-actions {
  width: 120px;
  white-space: nowrap;
}

.email-link {
  color: #1565c0;
  word-break: break-all;
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
}

.badge-unread {
  background: #fff3e0;
  color: #e65100;
}

.badge-read {
  background: #f5f5f5;
  color: #757575;
}

.empty-cell {
  text-align: center;
  color: #999;
  padding: 32px !important;
}

.table-footer {
  margin: 0;
  padding: 10px 14px;
  font-size: 12px;
  color: #888;
  background: #fafafa;
}

.btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid transparent;
}

.btn-secondary {
  background: #f5f5f5;
  border-color: #ddd;
  color: #333;
}

.btn-ghost {
  background: transparent;
  border-color: #ddd;
  color: #444;
}

.btn-link {
  background: none;
  border: none;
  color: #1565c0;
  cursor: pointer;
  padding: 0 6px 0 0;
  font-size: 14px;
}

.btn-link.danger {
  color: #c62828;
}

.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.modal {
  background: #fff;
  border-radius: 10px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 24px;
}

.modal-wide {
  max-width: 640px;
}

.modal-title {
  margin: 0 0 20px;
  font-size: 18px;
}

.detail-grid {
  display: grid;
  grid-template-columns: 88px 1fr;
  gap: 8px 16px;
  margin: 0 0 20px;
  font-size: 14px;
}

.detail-grid dt {
  margin: 0;
  color: #666;
  font-weight: 500;
}

.detail-grid dd {
  margin: 0;
  word-break: break-word;
}

.detail-message h4 {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
}

.message-text {
  margin: 0;
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #eee;
  border-radius: 6px;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.6;
  max-height: 320px;
  overflow-y: auto;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}
</style>
